import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { ActionItemStatus } from "src/constants";
import { useSignalR } from "src/hooks";
import {
  ActionItemDetailDto,
  ActionItemSummaryDto,
  EditActionItemStatusDto,
  FetchError,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";

type Variables = {
  actionItemGuid: string;
  statusId: ActionItemStatus;
  planGuid: string;
};

const useEditActionItemStatusRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemStatusRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditActionItemStatusDto = {
      statusId: variables.statusId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/status`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemStatusRequest };
};

const useEditActionItemStatus = () => {
  const { editActionItemStatusRequest } = useEditActionItemStatusRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemStatusRequest,
    onMutate: async (variables) => {
      let cachedSummaryData: ActionItemSummaryDto | undefined;
      let cachedDetailData: ActionItemDetailDto | undefined;

      // TODO: Update user strategic ownership caches for action item mutations
      // let cachedUserStrategyData: { [userGuid: string]: GetUserStrategicOwnershipDto | undefined } = {};

      cachedSummaryData = await optimisticallyUpdateQueryData<ActionItemSummaryDto>(
        queryClient,
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        () => ({
          currentStatus: variables.statusId,
        })
      );

      cachedDetailData = await optimisticallyUpdateQueryData<ActionItemDetailDto>(
        queryClient,
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        () => ({
          currentStatus: variables.statusId,
        })
      );

      return {
        cachedSummaryData,
        cachedDetailData,
        // cachedUserStrategyData,
      };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
      signalR.sendRefetchPlan(variables.planGuid);

      // if (variables.ownerUserGuids) {
      //   variables.ownerUserGuids.forEach((userGuid) => {
      //     // This is overkill, but there's no way to tell whether this item is a strategic or task item at this time.
      //     signalR.sendRefetchUserTaskItems(userGuid);
      //     signalR.sendRefetchUserStrategicOwnershipItems(userGuid);
      //   });
      // }
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        context?.cachedSummaryData
      );

      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        context?.cachedDetailData
      );

      // if (variables.ownerUserGuids) {
      //   variables.ownerUserGuids.forEach((userGuid) => {
      //     queryClient.setQueryData(
      //       queryKeys.users.getUserStrategicOwnership(userGuid),
      //       context?.cachedUserStrategyData[userGuid]
      //     );
      //   });
      // }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));

      // if (variables.ownerUserGuids) {
      //   variables.ownerUserGuids.forEach((userGuid) => {
      //     queryClient.invalidateQueries(queryKeys.users.getUserTaskItems(userGuid));
      //     queryClient.invalidateQueries(queryKeys.users.getUserStrategicOwnership(userGuid));
      //   });
      // }
    },
  });
};

export { useEditActionItemStatus };
